<template>
  <div>
    <div v-if="loading">
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem"
        label="Large Spinner"
      ></b-spinner>
    </div>

    <b-row v-if="!loading">
      <div class="col-md-3 text-center text-md-right order-md-2">
        <div v-if="company.logo" class="pt-2">
          <v-lazy-image :src="company.logo" class="img-fluid" />
        </div>
      </div>
      <div class="col-md-9">
        <form-base
          :submit-status="submitStatus"
          action="Update"
          :errors="errors"
          :loading="loading"
          v-on:handleSubmit="handleSubmit"
          class="py-5"
        >
          <template v-slot:fields>
            <form-group label="Name" :required="true">
              <input
                type="text"
                @blur="$v.company.name.$touch"
                class="form-control"
                :class="{ 'is-invalid': $v.company.name.$error }"
                v-model="company.name"
              />

              <div class="invalid-feedback" v-if="!$v.company.name.required">
                Please enter name
              </div>
            </form-group>

            <form-group label="Code" :required="true">
              <input
                type="text"
                class="form-control"
                @blur="$v.company.code.$touch"
                :class="{ 'is-invalid': $v.company.code.$error }"
                v-model="company.code"
              />
              <div class="invalid-feedback" v-if="!$v.company.name.required">
                Please enter code
              </div>
            </form-group>

            <form-group label="Registration Number">
              <input
                type="text"
                class="form-control"
                v-model="company.registration_number"
              />
            </form-group>

            <form-group label="Contact Number">
              <input
                type="text"
                class="form-control"
                v-model="company.contact_number"
              />
            </form-group>

            <form-group label="Contact Number 2">
              <input
                type="text"
                class="form-control"
                v-model="company.contact_number_2"
              />
            </form-group>

            <form-group label="Contact Fax">
              <input
                type="text"
                class="form-control"
                v-model="company.contact_fax"
              />
            </form-group>

            <form-group label="Email Info">
              <input
                type="email"
                class="form-control"
                v-model="company.email_address_info"
              />
            </form-group>

            <form-group label="Email Accounts">
              <input
                type="email"
                class="form-control"
                v-model="company.email_address_accounts"
              />
            </form-group>

            <form-group label="Email Sales">
              <input
                type="email"
                class="form-control"
                v-model="company.email_address_sales"
              />
            </form-group>

            <form-group label="Website">
              <input
                type="text"
                class="form-control"
                v-model="company.website"
              />
            </form-group>

            <form-group label="Physical Address" :required="true">
              <textarea-autosize
                @blur="$v.company.physical_address.$touch"
                :class="{ 'is-invalid': $v.company.physical_address.$error }"
                class="form-control"
                v-model="company.physical_address"
              />
              <div
                class="invalid-feedback"
                v-if="!$v.company.physical_address.required"
              >
                Please enter address
              </div>
            </form-group>

            <form-group label="Postal Address">
              <textarea-autosize
                class="form-control"
                v-model="company.postal_address"
              />
            </form-group>

            <form-group label="Banking Details" :required="true">
              <textarea-autosize
                class="form-control"
                v-model="company.banking_details"
              />
            </form-group>

            <form-group label="Charges VAT">
              <b-form-checkbox
                size="lg"
                v-model="company.is_vatable"
                value="1"
                unchecked-value="0"
              >
              </b-form-checkbox>
            </form-group>

            <form-group
              label="VAT Rate%"
              v-if="company.is_vatable == 1"
              :required="true"
            >
              <input
                type="number"
                max="50"
                min="1"
                @blur="$v.company.vat_rate.$touch"
                :class="{ 'is-invalid': $v.company.vat_rate.$error }"
                class="form-control w-50"
                v-model="company.vat_rate"
              />
              <div
                class="invalid-feedback"
                v-if="!$v.company.vat_rate.required"
              >
                Please enter vat rate
              </div>
            </form-group>

            <form-group
              label="VAT Number"
              v-if="company.is_vatable == 1"
              :required="true"
            >
              <input
                type="text"
                @blur="$v.company.vat_number.$touch"
                :class="{ 'is-invalid': $v.company.vat_number.$error }"
                class="form-control"
                v-model="company.vat_number"
              />
              <div
                class="invalid-feedback"
                v-if="!$v.company.vat_number.required"
              >
                Please enter vat number
              </div>
            </form-group>

            <form-group label="Logo">
              <image-upload
                @files-uploaded="
                  company.image_id = $event;
                  imageUploading = false;
                "
                @files-uploaded-started="imageUploading = true"
                :multiple="false"
                :imageCrop="false"
                :validationState="imageVstate"
                acceptedTypes="image/*"
              />
            </form-group>
          </template>
        </form-base>
      </div>
    </b-row>
  </div>
</template>

<script>
import companyRepo from "@/api/repos/companyRepo";
import { required, requiredIf } from "vuelidate/lib/validators";
import formBase from "@/components/common/FormBase";
import formGroup from "@/components/common/FormGroup";
import imageUpload from "@/components/common/ImageUpload";

export default {
  components: {
    formBase,
    formGroup,
    imageUpload,
  },

  validations: {
    company: {
      name: {
        required,
      },
      code: {
        required,
      },
      physical_address: {
        required,
      },
      vat_rate: {
        required: requiredIf(function (company) {
          return company.is_vatable == 1;
        }),
      },
      vat_number: {
        required: requiredIf(function (company) {
          return company.is_vatable == 1;
        }),
      },
    },
  },

  mounted() {
    this.getCompany();
  },

  props: ["id"],

  data() {
    return {
      company: {},
      loading: false,
      errors: null,
      submitStatus: null,
      imageUploading: false,
    };
  },

  methods: {
    async getCompany() {
      try {
        this.loading = true;
        let { data } = await companyRepo.get(this.id);
        this.company = data.company;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async handleSubmit() {
      this.loading = true;
      this.submitStatus = "PENDING";
      this.$v.$touch();
      if (this.$v.$invalid || this.imageUploading) {
        if (this.imageUploading) alert("images uploading...");
        this.loading = false;
        this.submitStatus = null;
      } else {
        try {
          await companyRepo.update(this.id, this.company);
          this.$root.$bvToast.toast("Company Updated", {
            title: "Success",
            autoHideDelay: 3000,
            variant: "success",
          });
          this.$router.push({ name: "companies" });
        } catch (error) {
          console.log(error);
          if (error.response.status === 422) {
            this.errors = error.response.data.errors;
            this.$root.$bvToast.toast("Form Errors", {
              title: "Errors on form data",
              autoHideDelay: 3000,
              variant: "danger",
            });
          }
        } finally {
          this.loading = false;
          this.submitStatus = null;
        }
      }
    },
  },

  computed: {
    imageVstate: function () {
      return null;
    },
  },
};
</script>
